// VRP API Service

import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL, API_ENDPOINTS } from './constants/app.constants';
import { ApiResponse, HealthResponse, StatsResponse } from './domain/api.types';
import { DistanceMatrix, RouteGeometry, VRPRequest, VRPSolution } from './domain/domain.types';

class VRPApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 120000, // 2 minutes timeout for VRP solving
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
      },
      (error) => {
        console.error('❌ Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        return response;
      },
      (error) => {
        console.error('❌ Response Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  

  async solveVRP(request: VRPRequest): Promise<ApiResponse<VRPSolution>> {
    try {
      console.log('🚀 VRP API Service: solveVRP called');
      console.log('📊 Request data:', request);

      // Yeni API formatına uygun request hazırla
      // Sadece gerekli ve opsiyonel alanları gönder
      const apiRequest: any = {
        locations: request.locations,
        vehicles: request.vehicles
      };

      // Opsiyonel alanları ekle (varsa)
      if (request.backend) {
        apiRequest.backend = request.backend;
      }
      if (request.solver_type) {
        apiRequest.solver_type = request.solver_type;
      }
      if (request.use_route_optimization !== undefined) {
        apiRequest.use_route_optimization = request.use_route_optimization;
      }
      if (request.valhallaConfig) {
        apiRequest.valhallaConfig = request.valhallaConfig;
      }
      if (request.start_time) {
        apiRequest.start_time = request.start_time;
      }
      if (request.end_time) {
        apiRequest.end_time = request.end_time;
      }
      if (request.service_duration !== undefined) {
        apiRequest.service_duration = request.service_duration;
      }
      if (request.lunch_break !== undefined) {
        apiRequest.lunch_break = request.lunch_break;
      }
      if (request.lunch_break_start) {
        apiRequest.lunch_break_start = request.lunch_break_start;
      }
      if (request.lunch_break_end) {
        apiRequest.lunch_break_end = request.lunch_break_end;
      }
      if (request.ignoreConstraints !== undefined) {
        apiRequest.ignoreConstraints = request.ignoreConstraints;
      }
      if (request.constraints) {
        apiRequest.constraints = request.constraints;
      }

      console.log('📤 API request:', apiRequest);
      console.log('📍 Locations count:', apiRequest.locations.length);
      console.log('🚛 Vehicles count:', apiRequest.vehicles.length);

      // VRP solve endpoint'ini kullan
      const response = await this.api.post(API_ENDPOINTS.SOLVE_VRP, apiRequest);

      console.log('✅ VRP solve response:', response.data);

      if (response.data?.status === 'success') {
        const solution = response.data;

        console.log('🎯 VRP Solution received:', solution);
        console.log('🚛 Rota sayısı:', solution.cars?.length || 0);

        // Backend'den gelen response'u doğrudan döndür
        return {
          data: {
            cars: solution.cars || [],
            summary: solution.summary || {},
            status: solution.status || 'success',
            sequence_order: solution.sequence_order || []
          } as VRPSolution,
          status: response.status,
        };
      } else {
        console.error('❌ VRP solve failed:', response.data);
        throw new Error('VRP solving failed');
      }
    } catch (error: any) {
      console.error('💥 solveVRP error:', error);
      return {
        error: error.response?.data?.error || error.message,
        status: error.response?.status || 500,
      };
    }
  }

 


  async editRoute(request: any): Promise<ApiResponse<VRPSolution>> {
    try {
      console.log('🔧 VRP API Service: editRoute called');
      console.log('📊 Edit request data:', request);

      const response = await this.api.post('/api/vrp/edit-route', request);

      console.log('✅ Edit route response:', response.data);

      return {
        data: response.data,
        status: response.status,
      };
    } catch (error: any) {
      console.error('💥 editRoute error:', error);
      return {
        error: error.response?.data?.error || error.message,
        status: error.response?.status || 500,
      };
    }
  }

  async optimizeRouteWithAI(request: any): Promise<ApiResponse<any>> {
    try {
      console.log('🤖 VRP API Service: optimizeRouteWithAI called');
      console.log('📊 AI Optimization request data:', request);

      const response = await this.api.post('/api/vrp/ai-optimized-route', request);

      console.log('✅ AI Optimization response:', response.data);

      return {
        data: response.data,
        status: response.status,
      };
    } catch (error: any) {
      console.error('💥 optimizeRouteWithAI error:', error);
      return {
        error: error.response?.data?.error || error.message,
        status: error.response?.status || 500,
      };
    }
  }


}

export const vrpApiService = new VRPApiService();
